import {
  Users,
  CalendarDays,
  Mail,
  Calculator,
  CreditCard,
  CalendarCheck,
  SquareCheck,
  FileText,
  MessageSquareText,
  MailOpen,
  Workflow,
} from "lucide-react";
import { Chapter } from "./Chapter";
import { Reveal } from "./Reveal";

const GoogleMark = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M23.5 12.27c0-.85-.08-1.66-.22-2.45H12v4.64h6.45a5.52 5.52 0 0 1-2.39 3.62v3h3.87c2.26-2.09 3.57-5.16 3.57-8.81z"/>
    <path fill="#34A853" d="M12 24c3.24 0 5.96-1.07 7.94-2.91l-3.87-3c-1.07.72-2.44 1.15-4.07 1.15-3.13 0-5.78-2.11-6.73-4.96H1.29v3.1A12 12 0 0 0 12 24z"/>
    <path fill="#FBBC05" d="M5.27 14.28A7.2 7.2 0 0 1 4.89 12c0-.79.14-1.56.38-2.28v-3.1H1.29a12 12 0 0 0 0 10.76l3.98-3.1z"/>
    <path fill="#EA4335" d="M12 4.76c1.76 0 3.34.6 4.58 1.8l3.44-3.44A11.98 11.98 0 0 0 12 0 12 12 0 0 0 1.29 6.62l3.98 3.1C6.22 6.87 8.87 4.76 12 4.76z"/>
  </svg>
);

const MicrosoftMark = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
    <rect x="1" y="1" width="10.5" height="10.5" fill="#F25022" />
    <rect x="12.5" y="1" width="10.5" height="10.5" fill="#7FBA00" />
    <rect x="1" y="12.5" width="10.5" height="10.5" fill="#00A4EF" />
    <rect x="12.5" y="12.5" width="10.5" height="10.5" fill="#FFB900" />
  </svg>
);

const QuickBooksMark = () => (
  <svg width="26" height="26" viewBox="0 0 26 26" aria-hidden="true">
    <circle cx="13" cy="13" r="13" fill="#2CA01C" />
    <text x="13" y="17.5" textAnchor="middle" fontSize="11" fontWeight="700" fill="#FFFFFF" fontFamily="Inter, sans-serif">qb</text>
  </svg>
);

const LineIcon = ({ icon: Icon }) => (
  <Icon size={24} strokeWidth={1.5} className="text-[#2563EB]" />
);

const KEEP_TOOLS = [
  { label: "Google Workspace", Mark: GoogleMark },
  { label: "Microsoft 365", Mark: MicrosoftMark },
  { label: "QuickBooks", Mark: QuickBooksMark },
  { label: "CRM", icon: Users },
  { label: "Calendar", icon: CalendarDays },
  { label: "Email", icon: Mail },
  { label: "Accounting", icon: Calculator },
  { label: "Payments", icon: CreditCard },
];

const NOVA_APPS = [
  { label: "Customer Management", icon: Users },
  { label: "Scheduling", icon: CalendarCheck },
  { label: "Tasks", icon: SquareCheck },
  { label: "Invoicing", icon: FileText },
  { label: "Forms", icon: MessageSquareText },
  { label: "Customer Requests", icon: MailOpen },
];

const Tile = ({ label, testid, variant, children }) => (
  <div
    data-testid={testid}
    className={`group/tile flex items-center gap-2.5 rounded-2xl border bg-white px-3 py-4 transition-[transform,border-color,box-shadow,background-color] duration-300 ease-out hover:-translate-y-1 hover:shadow-[0_12px_28px_-10px_rgb(7,26,56,0.16)] ${
      variant === "nova"
        ? "border-[#E7EEF9] hover:border-[#7dd0f0] hover:bg-[#EFF6FF]"
        : "border-[#E7EEF9] hover:border-[#93C5FD]"
    }`}
  >
    <span className="w-8 h-8 flex items-center justify-center shrink-0 transition-transform duration-300 ease-out group-hover/tile:scale-110">
      {children}
    </span>
    <span className="text-xs xl:text-[13px] font-medium text-[#071A38] leading-snug min-w-0 whitespace-nowrap">{label}</span>
  </div>
);

export const ConnectOrNova = () => (
  <section
    id="your-stack"
    data-testid="connect-or-nova-section"
    className="scroll-mt-36 bg-white"
  >
    <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24 lg:py-32">
      <Chapter
        number="02"
        label="Nova Connects"
        wide
        title={
          <>
            Already Have It? <span className="text-nova-gradient">Keep It.</span>
            <br />
            Need It? <span className="text-nova-gradient">Nova Has It.</span>
          </>
        }
        sub="Use the business apps you already rely on, or use Nova's built-in apps whenever you need them."
      >
        <div className="mt-16 flex flex-col xl:grid xl:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] xl:gap-4 gap-12 items-center max-w-2xl mx-auto xl:max-w-none">
          <Reveal x={-24}>
              <div className="rounded-[22px] border border-[#E3EDFB] bg-white p-6 shadow-[0_8px_30px_rgb(7,26,56,0.05)]"
              data-testid="keep-tools-panel"
            >
              <h3 className="text-center text-sm font-bold tracking-[0.18em] uppercase text-[#2563EB] mb-7">
                Keep Your Tools
              </h3>
              <div className="grid grid-cols-1 min-[480px]:grid-cols-2 gap-3.5">
                {KEEP_TOOLS.map((t) => (
                  <Tile
                    key={t.label}
                    label={t.label}
                    testid={`keep-tool-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {t.Mark ? <t.Mark /> : <LineIcon icon={t.icon} />}
                  </Tile>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.12} y={16} className="flex flex-col items-center">
            <div className="group/nova relative flex flex-col items-center" data-testid="nova-choice-center">
              <div className="relative flex items-center justify-center">
                <span className="absolute w-44 h-44 rounded-full border border-[#DBEAFE] transition-transform duration-500 ease-out group-hover/nova:scale-110 motion-reduce:transform-none" />
                <span className="absolute w-56 h-56 rounded-full border border-[#EFF6FF] transition-transform duration-700 ease-out group-hover/nova:scale-[1.07] motion-reduce:transform-none" />
                <img
                  src="/nova-mark-official.png"
                  alt="Nova"
                  data-testid="nova-official-logo"
                  className="relative w-28 select-none transition-[filter,transform] duration-500 ease-out group-hover/nova:brightness-110 group-hover/nova:rotate-[1.5deg] motion-reduce:transform-none"
                  draggable={false}
                />
              </div>
              <div className="mt-6 flex items-center gap-4" data-testid="nova-or">
                <span className="h-px w-10 bg-gradient-to-r from-transparent to-[#7C2CF3]" />
                <span className="text-lg font-extrabold tracking-wide text-nova-gradient">OR</span>
                <span className="h-px w-10 bg-gradient-to-l from-transparent to-[#06CFE8]" />
              </div>
            </div>
          </Reveal>

          <Reveal x={24}>
            <div
              data-testid="nova-apps-panel"
              className="rounded-[22px] border border-[#E3EDFB] bg-white p-6 shadow-[0_8px_30px_rgb(7,26,56,0.05)]"
            >
              <h3 className="text-center text-sm font-bold tracking-[0.18em] uppercase mb-7 text-nova-gradient">
                Use Nova's Built-In Apps
              </h3>
              <div className="grid grid-cols-1 min-[480px]:grid-cols-2 gap-3.5">
                {NOVA_APPS.map((t) => (
                  <Tile
                    key={t.label}
                    label={t.label}
                    variant="nova"
                    testid={`nova-app-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <LineIcon icon={t.icon} />
                  </Tile>
                ))}
              </div>
              <div className="mt-3.5">
                <Tile label="Team Workflows" variant="nova" testid="nova-app-team-workflows">
                  <LineIcon icon={Workflow} />
                </Tile>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.2} className="mt-16">
          <div className="flex items-center justify-center gap-5" data-testid="connect-message">
            <span className="hidden sm:block h-px w-24 lg:w-44 bg-gradient-to-r from-transparent via-[#93C5FD] to-[#2563EB]" />
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" className="shrink-0">
              <defs>
                <linearGradient id="spark-grad" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#7C2CF3" />
                  <stop offset="50%" stopColor="#2563FF" />
                  <stop offset="100%" stopColor="#06CFE8" />
                </linearGradient>
              </defs>
              <path d="M12 2l2.4 7.6L22 12l-7.6 2.4L12 22l-2.4-7.6L2 12l7.6-2.4z" fill="url(#spark-grad)" />
            </svg>
            <p className="text-base text-nova-slate">
              Your tools. Nova's tools.{" "}
              <span className="font-semibold text-nova-gradient">Your choice.</span>
            </p>
            <span className="hidden sm:block h-px w-24 lg:w-44 bg-gradient-to-l from-transparent via-[#93C5FD] to-[#06CFE8]" />
          </div>
        </Reveal>
      </Chapter>
    </div>
  </section>
);
